/*
 * Poly2Tri Copyright (c) 2009-2014, Poly2Tri Contributors
 * http://code.google.com/p/poly2tri/
 *
 * poly2tri.js (JavaScript port) (c) 2009-2014, Poly2Tri Contributors
 * https://github.com/r3mi/poly2tri.js
 *
 * All rights reserved.
 *
 * Distributed under the 3-clause BSD License, see LICENSE.txt
 */

import type { XY } from './xy'
import type { Triangle } from './triangle'

/**
 * Advancing front node
 */
export class Node {
	point: XY
	triangle: Triangle | null
	next: Node | null = null
	prev: Node | null = null
	value: number

	constructor(p: XY, t?: Triangle) {
		this.point = p
		this.triangle = t || null
		this.value = p.x
	}
}

/**
 * Advancing front
 */
export class AdvancingFront {
	private head_: Node
	private tail_: Node
	private search_node_: Node

	constructor(head: Node, tail: Node) {
		this.head_ = head
		this.tail_ = tail
		this.search_node_ = head
	}

	head(): Node {
		return this.head_
	}

	setHead(node: Node): void {
		this.head_ = node
	}

	tail(): Node {
		return this.tail_
	}

	setTail(node: Node): void {
		this.tail_ = node
	}

	search(): Node {
		return this.search_node_
	}

	setSearch(node: Node): void {
		this.search_node_ = node
	}

	findSearchNode(): Node {
		// TODO: implement BST index
		return this.search_node_
	}

	locateNode(x: number): Node | null {
		let node: Node | null = this.search_node_

		if (x < node.value) {
			while (node = node.prev) {
				if (x >= node.value) {
					this.search_node_ = node
					return node
				}
			}
		} else {
			while (node = node.next) {
				if (x < node.value) {
					this.search_node_ = node.prev!
					return node.prev
				}
			}
		}
		return null
	}

	locatePoint(point: XY): Node | null {
		const px = point.x
		let node: Node | null = this.findSearchNode()
		const nx = node.point.x

		if (px === nx) {
			// Here we are comparing point references, not values
			if (point !== node.point) {
				// We might have two nodes with same x value for a short time
				if (point === node.prev?.point) {
					node = node.prev
				} else if (point === node.next?.point) {
					node = node.next
				} else {
					throw new Error('poly2tri Invalid AdvancingFront.locatePoint() call')
				}
			}
		} else if (px < nx) {
			while (node = node.prev) {
				if (point === node.point) {
					break
				}
			}
		} else {
			while (node = node.next) {
				if (point === node.point) {
					break
				}
			}
		}

		if (node) {
			this.search_node_ = node
		}
		return node
	}
}

